import {
  InputGroup,
  Input,
  InputLeftElement,
  InputRightElement,
  Icon,
  useColorModeValue,
  Box,
  Button,
  List,
  ListItem,
  Text,
  Flex,
  SlideFade,
  Select,
  chakra,
  HStack,
  Badge,
  ButtonGroup,
  IconButton,
  VStack,
  useBreakpointValue
} from '@chakra-ui/react';
import { MagnifyingGlass, MapPin, TextAa, X, SquaresFour, Columns, Rectangle } from '@phosphor-icons/react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { getSuggestions } from '../utils/elasticsearchApi';
import '../styles/ElasticSearchBar.css';

export default function ElasticSearchBar({ 
  initialValue = ''
}) {
  const [searchValue, setSearchValue] = useState(initialValue);
  const [searchType, setSearchType] = useState('location'); // 'location' or 'name'
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const isElasticsearchPage = location.pathname === '/search-elasticsearch';
  const targetSearchPage = isElasticsearchPage ? '/search-elasticsearch' : '/search';

  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false });
  const searchBarSize = useBreakpointValue({ base: 'md', md: 'lg' });

  const inputBg = useColorModeValue('bg.surface', 'orange.900');
  const borderColor = useColorModeValue('border.default', 'orange.700');
  const hoverBorderColor = useColorModeValue('border.default', 'orange.600');
  const buttonBg = useColorModeValue('bg.surface', 'bg.surface');
  const buttonActiveBg = useColorModeValue('bg.accent', 'bg.accent');
  const buttonActiveColor = useColorModeValue('text.default', 'text.default');

  useEffect(() => {
    if (searchValue.length > 2 && searchType === 'name') {
      const fetchSuggestions = async () => {
        try {
          const elasticsearchSuggestions = await getSuggestions(searchValue, 'name');
          if (elasticsearchSuggestions && elasticsearchSuggestions.length > 0) {
            const transformedSuggestions = elasticsearchSuggestions.slice(0, 5).map((suggestion, index) => ({
              id: `suggestion-${index}`,
              name: suggestion.text || suggestion,
            }));
            setSuggestions(transformedSuggestions);
            setShowSuggestions(transformedSuggestions.length > 0);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
          setShowSuggestions(false);
        }
      };
      fetchSuggestions();
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchValue, searchType]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      const currentParams = Object.fromEntries(searchParams.entries());
      navigate({
        pathname: targetSearchPage,
        search: new URLSearchParams({
          ...currentParams,
          [searchType]: searchValue.trim(),
        }).toString(),
      });
    }
  };

  const handleSuggestionClick = (siteName) => {
    setSearchValue(siteName);
    setShowSuggestions(false);
    handleSubmit(new Event('submit'));
  };

  const clearSearch = () => {
    setSearchValue('');
    setShowSuggestions(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setTimeout(() => {
      if (!document.activeElement?.closest('.search-suggestions')) {
        setShowSuggestions(false);
      }
    }, 200);
  };

  // Mobile-optimized search bar with stacked layout
  if (isMobile) {
    return (
      <Box position="relative" w="100%" className="mobile-search-container">
        <chakra.form onSubmit={handleSubmit} w="100%">
          <VStack spacing={3} align="stretch">
            {/* Search Type Selector - Mobile Toggle */}
            <Flex 
              bg={buttonBg}
              borderRadius="xl"
              p={1}
              align="center"
              justify="space-between"
            >
              <Button
                flex={1}
                bg={searchType === 'location' ? 'interactive.primary' : 'transparent'}
                color={searchType === 'location' ? 'white' : 'text.default'}
                onClick={() => setSearchType('location')}
                leftIcon={<Icon as={MapPin} boxSize={4} />}
                fontWeight="600"
                fontSize="sm"
                minH="40px"
                borderRadius="lg"
                border="none"
                _hover={{
                  bg: searchType === 'location' ? 'interactive.primary' : 'bg.accent',
                }}
                _focus={{
                  outline: 'none',
                  boxShadow: 'none',
                }}
                transition="all 0.2s ease"
              >
                Location
              </Button>
              <Button
                flex={1}
                bg={searchType === 'name' ? 'interactive.primary' : 'transparent'}
                color={searchType === 'name' ? 'white' : 'text.default'}
                onClick={() => setSearchType('name')}
                leftIcon={<Icon as={TextAa} boxSize={4} />}
                fontWeight="600"
                fontSize="sm"
                minH="40px"
                borderRadius="lg"
                border="none"
                ml={1}
                _hover={{
                  bg: searchType === 'name' ? 'interactive.primary' : 'bg.accent',
                }}
                _focus={{
                  outline: 'none',
                  boxShadow: 'none',
                }}
                transition="all 0.2s ease"
              >
                Site Name
              </Button>
            </Flex>

            {/* Search Input - Mobile Optimized */}
            <InputGroup size={searchBarSize} shadow="sm">
              <InputLeftElement>
                <Icon as={MagnifyingGlass} color="gray.400" boxSize={5} />
              </InputLeftElement>
              <Input
                ref={inputRef}
                variant="outline"
                placeholder={
                  searchType === 'location' 
                    ? 'Enter city, state, or country...' 
                    : 'Enter megalithic site name...'
                }
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                pl="3rem"
                pr="3rem"
                bg={inputBg}
                borderColor={isFocused ? hoverBorderColor : borderColor}
                borderWidth="2px"
                borderRadius="xl"
                fontSize="16px" // Prevents iOS zoom
                minH="48px"
                fontWeight="medium"
                _hover={{ borderColor: hoverBorderColor }}
                _focus={{
                  borderColor: hoverBorderColor,
                  boxShadow: `0 0 0 1px ${hoverBorderColor}`,
                }}
              />
              <InputRightElement>
                {searchValue ? (
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={clearSearch}
                    icon={<Icon as={X} boxSize={4} />}
                    aria-label="Clear search"
                    borderRadius="full"
                    _hover={{ bg: 'bg.accent' }}
                  />
                ) : null}
              </InputRightElement>
            </InputGroup>
          </VStack>
        </chakra.form>

        {/* Mobile-optimized suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <SlideFade in={showSuggestions} offsetY="10px">
            <Box
              position="absolute"
              width="100%"
              mt={2}
              bg={inputBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="xl"
              shadow="xl"
              zIndex="dropdown"
              className="search-suggestions mobile-suggestions"
              maxH="300px"
              overflowY="auto"
            >
              <List spacing={0}>
                {suggestions.map((site, index) => (
                  <ListItem
                    key={site.id}
                    p={4}
                    borderBottomWidth={index < suggestions.length - 1 ? "1px" : "0"}
                    borderColor={borderColor}
                    _hover={{ bg: 'bg.accent', cursor: 'pointer' }}
                    onClick={() => handleSuggestionClick(site.name)}
                    transition="background-color 0.2s"
                    minH="56px"
                    display="flex"
                    alignItems="center"
                  >
                    <Flex align="center" w="100%">
                      <Icon as={TextAa} mr={3} color="gray.500" boxSize={5} />
                      <Text fontWeight="medium" fontSize="md" noOfLines={2}>
                        {site.name}
                      </Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            </Box>
          </SlideFade>
        )}
      </Box>
    );
  }

  // Desktop layout (original)
  return (
    <Box position="relative" w="100%">
      <chakra.form onSubmit={handleSubmit} w="100%">
        <InputGroup size="lg" shadow="md" borderRadius="md">
          <InputLeftElement width="9rem">
            <Select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              variant="unstyled"
              className="search-type-select"
              iconColor="gray.400"
              height="100%"
              pl={4}
              fontSize="md"
              fontWeight="medium"
              color="text.muted"
            >
              <option value="location">Location</option>
              <option value="name">Name</option>
            </Select>
          </InputLeftElement>
          <Input
            ref={inputRef}
            variant="outline"
            placeholder={searchType === 'location' ? 'City, state, or country...' : 'Megalithic site name...'}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            pl="9.5rem"
            pr="3.5rem"
            bg={inputBg}
            borderColor={isFocused ? hoverBorderColor : borderColor}
            _hover={{ borderColor: hoverBorderColor }}
          />
          <InputRightElement width="3.5rem">
            {searchValue ? (
              <Button size="sm" variant="ghost" onClick={clearSearch}>
                <Icon as={X} />
              </Button>
            ) : (
              <Icon as={MagnifyingGlass} color="gray.400" />
            )}
          </InputRightElement>
        </InputGroup>
      </chakra.form>

      {showSuggestions && suggestions.length > 0 && (
        <SlideFade in={showSuggestions} offsetY="10px">
          <Box
            position="absolute"
            width="100%"
            mt={2}
            bg={inputBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            shadow="lg"
            zIndex="dropdown"
            className="search-suggestions"
          >
            <List spacing={0}>
              {suggestions.map((site) => (
                <ListItem
                  key={site.id}
                  p={3}
                  borderBottomWidth="1px"
                  borderColor={borderColor}
                  _hover={{ bg: 'bg.accent', cursor: 'pointer' }}
                  onClick={() => handleSuggestionClick(site.name)}
                  transition="background-color 0.2s"
                >
                  <Flex align="center">
                    <Icon as={TextAa} mr={3} color="gray.500" />
                    <Text fontWeight="medium">{site.name}</Text>
                  </Flex>
                </ListItem>
              ))}
            </List>
          </Box>
        </SlideFade>
      )}
    </Box>
  );
}
